import { createContext, useCallback, useContext, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

export interface ConfirmOptions {
  title: string;
  description?: string;
  /** Label on the confirm button. Default: "Confirm" / "Delete" when destructive. */
  confirmLabel?: string;
  /** Label on the cancel button. Default: "Cancel". */
  cancelLabel?: string;
  /** When true, the confirm button renders with red styling. */
  destructive?: boolean;
}

type Resolve = (result: boolean) => void;

interface ConfirmState {
  options: ConfirmOptions;
  resolve: Resolve;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Returns an async `confirm(options)` function. Resolves `true` if the user
 * clicks confirm, `false` on cancel / escape / backdrop. Mount one
 * `<ConfirmProvider>` high in the tree (AppShell does this) and use this
 * hook anywhere underneath.
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Suspend user?", destructive: true })) {
 *     suspend();
 *   }
 */
export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) {
    throw new Error("useConfirm must be used inside <ConfirmProvider>");
  }
  return fn;
}

/**
 * Single AlertDialog instance hosted at the app root. Spawning is imperative
 * (via the returned `confirm()` function), so call sites don't need to
 * manage open state themselves.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConfirmState | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setState({ options, resolve });
    });
  }, []);

  const close = (result: boolean) => {
    if (state) state.resolve(result);
    setState(null);
  };

  const { options } = state ?? { options: null };
  const destructive = options?.destructive ?? false;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={state !== null}
        onOpenChange={(next) => {
          if (!next) close(false);
        }}
      >
        {options && (
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{options.title}</AlertDialogTitle>
              {options.description && (
                <AlertDialogDescription>{options.description}</AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => close(false)}>
                {options.cancelLabel ?? "Cancel"}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => close(true)}
                className={cn(destructive && "bg-destructive text-white hover:bg-destructive/90")}
              >
                {options.confirmLabel ?? (destructive ? "Delete" : "Confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
